/* eslint-disable multiline-ternary */
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import { Spinner } from 'reactstrap'
import * as Yup from 'yup'

import authApi from '_api/auth'
import reportApi from '_api/report'
import SocialButton from '_components/SocialButton'
import { GOOGLE } from '_utils/constant'
import { getSocialToken, getUserInfo, removeSocialToken, setUserToken } from '_utils/localData'
import './style.scss'

const SignUpPage = () => {
  const history = useHistory()
  const location = useLocation()
  const { addToast } = useToasts()

  // Host query parameters from navigation state or map join link
  const groupInfo = location.state?.groupInfo
  const searchParams = new URLSearchParams(location.search)
  const hostName = groupInfo?.host || searchParams.get('host')
  const hostedBy = groupInfo?.hostname || searchParams.get('hostname')
  const approvalMode = groupInfo?.approval || searchParams.get('approval') || 'open'
  const joinQuestion = groupInfo?.question || searchParams.get('question') || ''

  // Clear navigation state on mount so that refreshing (F5) resets to a clean form
  useEffect(() => {
    if (location.state?.groupInfo) {
      window.history.replaceState({}, document.title)
    }
  }, [])

  // Form states
  const [isSubmittingForm, setIsSubmittingForm] = useState(false)
  const [showPin, setShowPin] = useState(false)
  const [showConfirmPin, setShowConfirmPin] = useState(false)

  // Social Connect (Google) state
  const [socialUser, setSocialUser] = useState(null)

  // Validation Schema for normal registration
  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required('Please enter your full name.')
      .min(2, 'Name must be at least 2 characters.'),
    email: Yup.string()
      .trim()
      .email('Please enter a valid email address.')
      .required('Please enter a valid email address.'),
    phone: Yup.string()
      .trim()
      .required('Please enter a valid mobile number.')
      .matches(/^[0-9+\s-]{8,16}$/, 'Mobile number must be 8-16 digits.'),
    postalCode: Yup.string()
      .trim()
      .required('Please enter a valid 6-digit postal code.')
      .matches(/^[0-9]{6}$/, 'Please enter a valid 6-digit postal code.'),
    pin: Yup.string()
      .trim()
      .required('Please enter your 4-digit PIN.')
      .matches(/^[0-9]{4}$/, 'PIN must be exactly 4 digits.'),
    confirmPin: Yup.string()
      .trim()
      .required('Please confirm your 4-digit PIN.')
      .oneOf([Yup.ref('pin'), null], "PINs don't match."),
    terms: Yup.boolean().oneOf(
      [true],
      'Please agree to co·bee’s Terms of Service and Privacy Statement.'
    ),
    joinAnswer: Yup.string().test(
      'joinQuestionRequired',
      "Please answer the host's question.",
      (val) => {
        if (approvalMode === 'question' && joinQuestion) {
          return Boolean(val && val.trim().length > 0)
        }
        return true
      }
    ),
  })

  // Validation Schema for Social Connect
  const socialValidationSchema = Yup.object().shape({
    phone: Yup.string()
      .trim()
      .required('Please enter a valid mobile number.')
      .matches(/^[0-9+\s-]{8,16}$/, 'Mobile number must be 8-16 digits.'),
    postalCode: Yup.string()
      .trim()
      .required('Please enter a valid 6-digit postal code.')
      .matches(/^[0-9]{6}$/, 'Please enter a valid 6-digit postal code.'),
  })

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(socialUser ? socialValidationSchema : validationSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      postalCode: '',
      pin: '',
      confirmPin: '',
      terms: true,
      joinAnswer: '',
    },
  })

  // Handle Form Submit
  const onFormSubmit = async (data) => {
    // 1. Social Connect (Google)
    if (socialUser) {
      try {
        setIsSubmittingForm(true)
        const postData = {
          accessToken: getSocialToken(),
          email: socialUser.email,
          name: socialUser.name,
          phone: data.phone.trim(),
          postalCode: data.postalCode.trim(),
        }
        if (socialUser.social === GOOGLE) {
          postData.linkedGoogleAccount = socialUser.id
        }
        const res = await authApi.register(postData)
        setUserToken(res.msgResp.token)
        removeSocialToken()
        addToast('Registration successful! Welcome to co·bee.', {
          appearance: 'success',
          autoDismiss: true,
        })
        history.push('/welcome')
      } catch (err) {
        setIsSubmittingForm(false)
        addToast(err?.msgResp || 'Registration failed', {
          appearance: 'error',
          autoDismiss: true,
        })
      }
      return
    }

    // 2. Normal registration: check email uniqueness, then register with 4-digit PIN
    try {
      setIsSubmittingForm(true)
      const checkRes = await authApi.searchUser(data.email.toLowerCase().trim())
      if (checkRes?.msgResp?.uid) {
        setError('email', {
          type: 'manual',
          message: 'This email is already registered. Please sign in instead.',
        })
        setIsSubmittingForm(false)
        return
      }

      const payload = {
        name: data.name.trim(),
        email: data.email.toLowerCase().trim(),
        phone: data.phone.trim(),
        postalCode: data.postalCode.trim(),
        password: data.pin.trim(),
      }
      if (data.joinAnswer) {
        payload.joinAnswer = data.joinAnswer.trim()
      }

      const res = await authApi.register(payload)
      const token = res?.msgResp?.token
      if (token) {
        setUserToken(token)
        addToast('Registration successful! Welcome to co·bee.', {
          appearance: 'success',
          autoDismiss: true,
        })
        history.push('/welcome')
      }
    } catch (error) {
      setIsSubmittingForm(false)
      reportApi.report({ message: `[Signup 03]${error?.msgCode} - ${error?.msgResp}` })
      addToast(error?.msgResp || 'Registration failed. Please try again.', {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  return (
    <div className='signup-page-v2'>
      <nav className='signup-nav'>
        <Link to='/' className='logo'>
          co<span>·</span>bee
        </Link>
        <div className='nav-right'>
          Already have an account?{' '}
          <Link to={{ pathname: '/sign-in', state: { groupInfo } }}>Sign in</Link>
        </div>
      </nav>

      <div className='page-body'>
        <div className='form-wrap'>
          {socialUser ? (
            /* ─── SOCIAL CONNECT FORM (GOOGLE) ─── */
            <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
              <button type='button' className='btn-back' onClick={() => setSocialUser(null)}>
                ← Use a different method
              </button>
              <h2>Almost there!</h2>
              <p className='form-sub'>
                Please provide your contact details to complete registration.
              </p>

              <div className='social-profile-preview'>
                {socialUser.imageUrl && <img src={socialUser.imageUrl} alt={socialUser.name} />}
                <div className='user-info'>
                  <div className='user-name'>{socialUser.name}</div>
                  <div className='user-email'>{socialUser.email}</div>
                </div>
              </div>

              <div className={`field ${errors.phone ? 'has-error' : ''}`}>
                <label htmlFor='social-phone'>Mobile number</label>
                <input
                  id='social-phone'
                  type='tel'
                  placeholder='9123 4567'
                  autoComplete='tel'
                  className={errors.phone ? 'invalid' : ''}
                  {...register('phone')}
                />
                <div className='field-hint'>
                  We’ll only use this for order and collection updates.
                </div>
                {errors.phone && <div className='field-error'>{errors.phone.message}</div>}
              </div>

              <div className={`field ${errors.postalCode ? 'has-error' : ''}`}>
                <label htmlFor='social-postal'>Postal code</label>
                <input
                  id='social-postal'
                  type='text'
                  placeholder='e.g. 018956'
                  inputMode='numeric'
                  maxLength={6}
                  autoComplete='postal-code'
                  className={errors.postalCode ? 'invalid' : ''}
                  {...register('postalCode')}
                />
                <div className='field-hint'>
                  Helps us match you to group buys collecting near you.
                </div>
                {errors.postalCode && (
                  <div className='field-error'>{errors.postalCode.message}</div>
                )}
              </div>

              <button type='submit' className='btn-submit' disabled={isSubmittingForm}>
                {isSubmittingForm ? (
                  <Spinner color='light' size='sm' />
                ) : (
                  'Complete Registration 🐝'
                )}
              </button>
            </form>
          ) : (
            /* ─── SINGLE FORM REGISTRATION (AS PER DESIGN MOCKUP) ─── */
            <form onSubmit={handleSubmit(onFormSubmit)} noValidate>
              <h2>Create your account</h2>
              <p className='form-sub'>
                {hostName ? (
                  approvalMode === 'open' ? (
                    <>
                      Finish creating your account to join <strong>{hostName}</strong>.
                    </>
                  ) : (
                    <>
                      Finish creating your account to request to join <strong>{hostName}</strong>.
                    </>
                  )
                ) : (
                  'Free for members — no card required.'
                )}
              </p>
              {/* Top Host Chip */}
              {hostName && !socialUser && (
                <div className='host-chip'>
                  <div className='host-chip-emoji'>🎁</div>
                  <div>
                    <div className='host-chip-name'>{hostName}</div>
                    {hostedBy && <div className='host-chip-by'>Hosted by {hostedBy}</div>}
                  </div>
                </div>
              )}
              {approvalMode === 'question' && joinQuestion && (
                <div className={`field ${errors.joinAnswer ? 'has-error' : ''}`}>
                  <label htmlFor='joinAnswer'>{joinQuestion}</label>
                  <textarea
                    id='joinAnswer'
                    rows={3}
                    placeholder='Type your answer...'
                    className={errors.joinAnswer ? 'invalid' : ''}
                    {...register('joinAnswer')}
                  />
                  {errors.joinAnswer && (
                    <div className='field-error'>{errors.joinAnswer.message}</div>
                  )}
                </div>
              )}

              {/* Full Name */}
              <div className={`field ${errors.name ? 'has-error' : ''}`}>
                <label htmlFor='name'>Full name</label>
                <input
                  id='name'
                  type='text'
                  placeholder='Jane Tan'
                  autoComplete='name'
                  className={errors.name ? 'invalid' : ''}
                  {...register('name')}
                />
                {errors.name && <div className='field-error'>{errors.name.message}</div>}
              </div>

              {/* Email Address */}
              <div className={`field ${errors.email ? 'has-error' : ''}`}>
                <label htmlFor='email'>Email address</label>
                <input
                  id='email'
                  type='email'
                  placeholder='jane@email.com'
                  autoComplete='email'
                  className={errors.email ? 'invalid' : ''}
                  {...register('email')}
                />
                {errors.email && <div className='field-error'>{errors.email.message}</div>}
              </div>

              <div className='signup-field-row'>
                {/* Mobile Number */}
                <div className={`field ${errors.phone ? 'has-error' : ''}`}>
                  <label htmlFor='phone'>Mobile number</label>
                  <input
                    id='phone'
                    type='tel'
                    placeholder='9123 4567'
                    autoComplete='tel'
                    className={errors.phone ? 'invalid' : ''}
                    {...register('phone')}
                  />
                  <div className='field-hint'>
                    We’ll only use this for order and collection updates.
                  </div>
                  {errors.phone && <div className='field-error'>{errors.phone.message}</div>}
                </div>

                {/* Postal Code */}
                <div className={`field ${errors.postalCode ? 'has-error' : ''}`}>
                  <label htmlFor='postal'>Postal code</label>
                  <input
                    id='postal'
                    type='text'
                    placeholder='e.g. 018956'
                    inputMode='numeric'
                    maxLength={6}
                    autoComplete='postal-code'
                    className={errors.postalCode ? 'invalid' : ''}
                    {...register('postalCode')}
                  />
                  <div className='field-hint'>
                    Helps us match you to group buys collecting near you.
                  </div>
                  {errors.postalCode && (
                    <div className='field-error'>{errors.postalCode.message}</div>
                  )}
                </div>
              </div>

              <div className='signup-field-row'>
                {/* 4-digit PIN */}
                <div className={`field ${errors.pin ? 'has-error' : ''}`}>
                  <label htmlFor='pin'>PIN</label>
                  <div className='pw-wrap'>
                    <input
                      id='pin'
                      type={showPin ? 'text' : 'password'}
                      placeholder='4-digit PIN'
                      inputMode='numeric'
                      maxLength={4}
                      autoComplete='new-password'
                      className={errors.pin ? 'invalid' : ''}
                      {...register('pin')}
                    />
                    <button
                      type='button'
                      className='pw-toggle'
                      onClick={() => setShowPin(!showPin)}
                    >
                      {showPin ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <div className='field-hint'>Use a 4-digit PIN for quick and secure access.</div>
                  {errors.pin && <div className='field-error'>{errors.pin.message}</div>}
                </div>

                {/* Confirm 4-digit PIN */}
                <div className={`field ${errors.confirmPin ? 'has-error' : ''}`}>
                  <label htmlFor='confirmPin'>Confirm PIN</label>
                  <div className='pw-wrap'>
                    <input
                      id='confirmPin'
                      type={showConfirmPin ? 'text' : 'password'}
                      placeholder='Re-enter your PIN'
                      inputMode='numeric'
                      maxLength={4}
                      autoComplete='new-password'
                      className={errors.confirmPin ? 'invalid' : ''}
                      {...register('confirmPin')}
                    />
                    <button
                      type='button'
                      className='pw-toggle'
                      onClick={() => setShowConfirmPin(!showConfirmPin)}
                    >
                      {showConfirmPin ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {errors.confirmPin && (
                    <div className='field-error'>{errors.confirmPin.message}</div>
                  )}
                </div>
              </div>

              {/* Terms Checkbox */}
              <div className='terms-row'>
                <input type='checkbox' id='terms' {...register('terms')} />
                <label htmlFor='terms'>
                  I agree to co·bee’s{' '}
                  <a href='/policy' target='_blank' rel='noreferrer'>
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href='/policy' target='_blank' rel='noreferrer'>
                    Privacy Statement
                  </a>
                  .
                </label>
              </div>
              {errors.terms && (
                <div className='field' style={{ marginTop: '-14px', marginBottom: '16px' }}>
                  <div className='field-error' style={{ display: 'block' }}>
                    {errors.terms.message}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button type='submit' className='btn-submit' disabled={isSubmittingForm}>
                {isSubmittingForm ? <Spinner color='light' size='sm' /> : 'Create my account 🐝'}
              </button>

              <div className='divider'>or</div>

              {/* Google Button */}
              <SocialButton
                social={GOOGLE}
                isLogin={false}
                setIsShowFormConnect={() => {
                  const info = getUserInfo()
                  if (info) setSocialUser(info)
                }}
                renderButton={(renderProps) => (
                  <button
                    type='button'
                    className='btn-google'
                    onClick={renderProps.onClick}
                    disabled={renderProps.disabled}
                  >
                    <svg width='16' height='16' viewBox='0 0 24 24'>
                      <path
                        fill='#4285F4'
                        d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                      />
                      <path
                        fill='#34A853'
                        d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                      />
                      <path
                        fill='#FBBC05'
                        d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                      />
                      <path
                        fill='#EA4335'
                        d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                      />
                    </svg>
                    Continue with Google
                  </button>
                )}
              />

              <p className='footer-link'>
                Want to host group buys instead? <a href='/#partner'>Apply as a host</a>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default SignUpPage
